import { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, Upload, X, RotateCcw, Check, AlertCircle } from 'lucide-react';

export default function FotoPazienteDialog({ open, onClose, onPhotoResult, currentPhoto }) {
  const [mode, setMode] = useState(null); // 'camera' | 'upload'
  const [preview, setPreview] = useState(currentPhoto || null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setStreaming(false);
  }, []);

  const startCamera = useCallback(async () => {
    setError('');
    setMode('camera');
    setPreview(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;
      setStreaming(true);
    } catch {
      setError('Impossibile accedere alla fotocamera. Verifica i permessi.');
    }
  }, []);

  // Assign stream to video element after it renders
  const videoCallbackRef = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
      node.play().catch(() => {});
    }
  }, []);

  const takePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
    setPreview(dataUrl);
    stopCamera();
  }, [stopCamera]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('File troppo grande (max 5MB)'); return; }
    if (!file.type.startsWith('image/')) { setError('Seleziona un file immagine'); return; }
    setError('');
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      setMode('upload');
    };
    reader.readAsDataURL(file);
  };

  const handleConfirm = () => {
    if (preview && onPhotoResult) {
      onPhotoResult(preview);
    }
    handleClose();
  };

  const handleClose = () => {
    stopCamera();
    setMode(null);
    setPreview(null);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="foto-paziente-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-brand" />
            Foto Paziente
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!mode && !preview && (
            <div className="flex flex-col gap-3 items-center py-4">
              <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                <Camera className="w-10 h-10 text-slate-300" />
              </div>
              <div className="flex gap-3">
                <Button onClick={startCamera} variant="outline" data-testid="foto-camera-btn">
                  <Camera className="w-4 h-4 mr-2" />Scatta Foto
                </Button>
                <label>
                  <Button variant="outline" asChild>
                    <span><Upload className="w-4 h-4 mr-2" />Carica File</span>
                  </Button>
                  <input type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-testid="foto-file-input" />
                </label>
              </div>
            </div>
          )}

          {/* Camera view */}
          {mode === 'camera' && streaming && !preview && (
            <div className="relative rounded-lg overflow-hidden bg-black">
              <video ref={videoCallbackRef} className="w-full rounded-lg" autoPlay playsInline muted />
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
                <Button onClick={takePhoto} size="sm" className="bg-white text-slate-800 hover:bg-slate-100" data-testid="take-photo-btn">
                  <Camera className="w-4 h-4 mr-1" />Scatta
                </Button>
                <Button onClick={() => { stopCamera(); setMode(null); }} size="sm" variant="ghost" className="text-white">
                  <X className="w-4 h-4 mr-1" />Annulla
                </Button>
              </div>
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="text-center space-y-3">
              <div className="relative inline-block">
                <img src={preview} alt="Anteprima" className="w-40 h-40 rounded-full object-cover mx-auto border-4 border-blue-100" data-testid="foto-preview" />
              </div>
              <div className="flex justify-center gap-2">
                <Button variant="outline" size="sm" onClick={() => { setPreview(null); setMode(null); }}>
                  <RotateCcw className="w-4 h-4 mr-1" />Riprova
                </Button>
              </div>
            </div>
          )}

          <canvas ref={canvasRef} className="hidden" />

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>Annulla</Button>
            {preview && (
              <Button onClick={handleConfirm} data-testid="confirm-foto-btn" className="bg-brand hover:bg-brand-dark text-white">
                <Check className="w-4 h-4 mr-1" />Conferma Foto
              </Button>
            )}
          </div>

          <p className="text-[10px] text-slate-400 leading-relaxed">
            Ai sensi dell'art. 9 del GDPR, la foto del paziente e un dato sensibile e viene acquisita
            previo consenso informato. L'immagine e conservata in modo sicuro e accessibile solo al
            personale autorizzato secondo il D.Lgs. 196/2003.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
